'use client';

import { FatalScreen } from '@/lib/components/error/fatal-screen';
import { referenceFor } from '@/lib/components/error/reference-id';
import posthog from 'posthog-js';
import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = { children: ReactNode };
type State = { hasError: boolean; referenceId?: string };

/**
 * Wraps the root-layout providers. Those errors fall outside route-level
 * `error.tsx`, so today they surface as Next's bare "Application error" with no
 * detail. This boundary captures the error AND the React componentStack (which
 * names the failing component) and reports it. React error boundaries must be
 * class components — there is no hook equivalent.
 */
export class ClientErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  // Derived here rather than in componentDidCatch so the reference is present on the very
  // first error render - there is no frame where the screen shows without it.
  static getDerivedStateFromError(error: Error & { digest?: string }): State {
    return { hasError: true, referenceId: referenceFor(error) };
  }

  override componentDidCatch(error: Error & { digest?: string }, info: ErrorInfo) {
    posthog.captureException(error, {
      source: 'root-error-boundary',
      digest: error.digest,
      componentStack: info.componentStack,
      reference_id: referenceFor(error),
    });
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex' }}>
          <FatalScreen referenceId={this.state.referenceId} />
        </div>
      );
    }
    return this.props.children;
  }
}
